import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import "./ContactModal.scss";
import { sendContactEmail } from "../utils/sendContactEmail";
import { 
  trackContactFormSubmit, 
  trackContactFormSuccess, 
  trackContactFormError 
} from "../utils/analytics";

// Make sure to bind modal to your appElement for better accessibility
if (typeof window !== "undefined") {
  Modal.setAppElement("#root");
}

const ContactModal = ({ isOpen, onRequestClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (showSuccessAnimation) {
      const timer = setTimeout(() => {
        setIsClosing(true);
        setTimeout(() => {
          onRequestClose();
          // Reset states after modal closes
          setTimeout(() => {
            setShowSuccessAnimation(false);
            setIsClosing(false);
            setSubmitStatus("");
            setFormData({
              name: "",
              email: "",
              company: "",
              message: "",
              phone: "",
            });
          }, 300);
        }, 500); // Wait for closing animation
      }, 5000); // Show success for 5 seconds

      return () => clearTimeout(timer);
    }
  }, [showSuccessAnimation, onRequestClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const sendEmailWithEmailJS = async (data) => {
    const result = await sendContactEmail({
      name: data.name,
      email: data.email,
      phone: data.phone,
      company: data.company,
      message: data.message,
      source: "lets-connect-modal",
    });
    if (result.success) {
      console.log("Email sent successfully:", result.result);
      return { success: true, message: "Email sent successfully!" };
    }
    console.error("Error sending email:", result.error);
    return { success: false, error: result.error };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("");

    // Track form submission attempt
    trackContactFormSubmit(formData);

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      const errorMessage = "Please fill in all required fields.";
      setSubmitStatus(errorMessage);
      trackContactFormError(errorMessage);
      setIsSubmitting(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      const errorMessage = "Please enter a valid email address.";
      setSubmitStatus(errorMessage);
      trackContactFormError(errorMessage);
      setIsSubmitting(false);
      return;
    }

    try {
      console.log("Sending email with EmailJS...");
      const result = await sendEmailWithEmailJS(formData);

      if (result.success) {
        setSubmitStatus("Thank you! Your message has been sent successfully.");
        setShowSuccessAnimation(true);
        setIsSubmitting(false);
        trackContactFormSuccess();
      } else {
        const errorMessage = "There was an error sending your message. Please try again.";
        setSubmitStatus(errorMessage);
        trackContactFormError(result.error || errorMessage);
        setIsSubmitting(false);
      }
    } catch (error) {
      const errorMessage = "There was an error sending your message. Please try again.";
      setSubmitStatus(errorMessage);
      trackContactFormError(error.message || errorMessage);
      setIsSubmitting(false);
    }
  };

  const modalStyles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.75)",
      zIndex: 1000,
      padding: "20px", // Add padding for mobile
    },
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      maxWidth: "650px", // Increased width
      width: "95%", // More responsive width
      maxHeight: "90vh", // Increased max height
      overflow: "auto",
      border: "none",
      borderRadius: "12px",
      padding: "0",
      background: "#fff",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    },
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={modalStyles}
      contentLabel="Contact Form"
    >
      <div className={`contact-modal ${showSuccessAnimation ? 'success-mode' : ''} ${isClosing ? 'closing' : ''}`}>
        {showSuccessAnimation ? (
          // Success Animation Component
          <div className="success-animation">
            <div className="success-checkmark">
              <div className="check-icon">
                <span className="icon-line line-tip"></span>
                <span className="icon-line line-long"></span>
                <div className="icon-circle"></div>
                <div className="icon-fix"></div>
              </div>
            </div>
            <div className="success-content">
              <h2>Thank You!</h2>
              <p>Your message has been sent successfully.</p>
              <p className="success-subtitle">We'll contact you shortly!</p>
              <div className="success-details">
                <div className="detail-item">
                  <span className="icon">👤</span>
                  <span>Name: {formData.name}</span>
                </div>
                <div className="detail-item">
                  <span className="icon">📧</span>
                  <span>Email: {formData.email}</span>
                </div>
              </div>
              <div className="auto-close-indicator">
                <div className="progress-bar"></div>
                <p>Closing in 5 seconds...</p>
              </div>
            </div>
          </div>
        ) : (
          // Original Form Component
          <>
            <div className="modal-header">
              <h2>Let's Build Something Amazing Together!</h2>
              <p className="modal-subtitle">
                Ready to bring your ideas to life? I specialize in developing 
                full-fledged mobile apps and web applications that deliver 
                exceptional user experiences.
              </p>
              <button
                className="close-button"
                onClick={onRequestClose}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <div className="services-highlight">
              <h3>🚀 What I Can Help You With:</h3>
              <ul>
                <li>📱 <strong>Mobile App Development</strong> - iOS & Android apps that users love</li>
                <li>💻 <strong>Web Application Development</strong> - Responsive, fast, and scalable web apps</li>
                <li>🎨 <strong>UI/UX Design</strong> - Beautiful interfaces that convert visitors to customers</li>
                <li>⚡ <strong>Full-Stack Solutions</strong> - From concept to deployment and beyond</li>
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Your company name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Tell Me About Your Project *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  placeholder="Describe your mobile app or web application idea. What problem does it solve? Who is your target audience? Any specific features you have in mind?"
                />
              </div>

              {submitStatus && !showSuccessAnimation && (
                <div
                  className={`submit-status ${
                    submitStatus.includes("successfully") ? "success" : "error"
                  }`}
                >
                  {submitStatus}
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  onClick={onRequestClose}
                  className="cancel-button"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ContactModal;
